import { Schema, Effect } from "effect";
import { HttpApiEndpoint, HttpApiError, HttpApiGroup, HttpApiSchema } from "effect/http-api";
import { OneFileSchema } from "../multipart.js";
import { DateTimeUtcFromString } from "../date-time.js";
import { clamp } from "../clamp.js";
import { UUID } from "../uuid.js";

export const ReportUploadResponseSchema = Schema.Struct({
  id: UUID,
  status: Schema.Literals(["pending", "processing", "completed", "failed"]),
  message: Schema.String,
  metadata: Schema.Struct({
    orgnr: Schema.String,
    reportType: Schema.Literals(["annual_report", "interim_report", "quarterly_report"]),
    fiscalYear: Schema.Number,
  }),
  createdAt: Schema.OptionFromNullOr(DateTimeUtcFromString),
});

export const AnnualReportProcessingProgressSchema = Schema.Struct({
  strategyVersion: Schema.OptionFromNullOr(Schema.String),
  pageCount: Schema.Number,
  totalChunks: Schema.Number,
  pendingChunks: Schema.Number,
  processingChunks: Schema.Number,
  completedChunks: Schema.Number,
  failedChunks: Schema.Number,
  estimatedInputTokens: Schema.OptionFromNullOr(Schema.Number),
  inputTokens: Schema.OptionFromNullOr(Schema.Number),
  outputTokens: Schema.OptionFromNullOr(Schema.Number),
  totalTokens: Schema.OptionFromNullOr(Schema.Number),
  failedChunkErrors: Schema.Array(
    Schema.Struct({
      chunkIndex: Schema.Number,
      pageStart: Schema.Number,
      pageEnd: Schema.Number,
      errorMessage: Schema.String,
    })
  ),
});

export const AnnualReportAnalysisResultSchema = Schema.Struct({
  meta: Schema.Struct({
    language: Schema.String,
    organizationNumber: Schema.String,
    companyName: Schema.String,
    reportType: Schema.String,
    fiscalYear: Schema.Number,
    fiscalPeriod: Schema.String,
    source: Schema.String,
  }),
  visualObservations: Schema.Array(
    Schema.Struct({
      page: Schema.Number,
      kind: Schema.Literals(["chart", "table", "photo", "diagram", "map", "logo", "other"]),
      description: Schema.String,
      extractedText: Schema.OptionFromNullOr(Schema.String),
      confidence: Schema.Number,
    })
  ),
  scenarios: Schema.Array(
    Schema.Struct({
      id: Schema.String,
      title: Schema.String,
      confidence: Schema.Number,
      amount: Schema.OptionFromNullOr(Schema.String),
      currency: Schema.OptionFromNullOr(Schema.String),
      date: Schema.OptionFromNullOr(Schema.String),
      entity: Schema.OptionFromNullOr(Schema.String),
      description: Schema.OptionFromNullOr(Schema.String),
      evidences: Schema.Array(
        Schema.Struct({
          text: Schema.String,
          page: Schema.Number,
        })
      ),
    })
  ),
});

export const ReportStatusResponseSchema = Schema.Struct({
  id: UUID,
  status: Schema.Literals(["pending", "processing", "completed", "failed"]),
  metadata: Schema.Struct({
    orgnr: Schema.String,
    reportType: Schema.Literals(["annual_report", "interim_report", "quarterly_report"]),
    fiscalYear: Schema.OptionFromNullOr(Schema.Number),
  }),
  createdAt: Schema.OptionFromNullOr(DateTimeUtcFromString),
  completedAt: Schema.OptionFromNullOr(DateTimeUtcFromString),
  extractedScenarios: Schema.optional(
    Schema.Array(
      Schema.Struct({
        id: Schema.OptionFromNullOr(Schema.String),
        scenario: Schema.OptionFromNullOr(Schema.String),
        confidence: Schema.OptionFromNullOr(Schema.Number),
        context: Schema.OptionFromNullOr(Schema.String),
        page: Schema.OptionFromNullOr(Schema.Number),
      })
    )
  ),
  error: Schema.OptionFromNullOr(Schema.String),
  progress: Schema.OptionFromNullOr(AnnualReportProcessingProgressSchema),
  analysis: Schema.OptionFromNullOr(AnnualReportAnalysisResultSchema),
});

const uploadIdParam = UUID;

export const ReportUploadPayloadSchema = Schema.Struct({
  file: OneFileSchema,
  orgnr: Schema.optional(Schema.String),
  fiscalYear: Schema.optional(Schema.NumberFromString),
  reportType: Schema.optional(
    Schema.Literals(["annual_report", "interim_report", "quarterly_report"])
  ),
}).pipe(
  HttpApiSchema.asMultipart({
    maxFileSize: 300 * 1024 * 1024, // 300MB
  })
);

export const AnnualReportDocumentProviderSchema = Schema.Literals([
  "manual_url",
  "syna",
  "bolagsapi",
  "bolagsverket",
]);

export const ReportImportPayloadSchema = Schema.Struct({
  provider: AnnualReportDocumentProviderSchema,
  sourceUrl: Schema.String,
  providerDocumentId: Schema.optional(Schema.String),
  fileName: Schema.optional(Schema.String),
  orgnr: Schema.optional(Schema.String),
  fiscalYear: Schema.optional(Schema.Finite),
  reportType: Schema.optional(
    Schema.Literals(["annual_report", "interim_report", "quarterly_report"])
  ),
});

// --- List endpoint schemas ---

export const AnnualReportEventItemSchema = Schema.Struct({
  eventId: UUID,
  scenarioId: Schema.String,
  scenarioTitle: Schema.String,
  confidence: Schema.Number,
  amount: Schema.OptionFromNullOr(Schema.String),
  currency: Schema.OptionFromNullOr(Schema.String),
  date: Schema.OptionFromNullOr(DateTimeUtcFromString),
  entity: Schema.OptionFromNullOr(Schema.String),
  description: Schema.OptionFromNullOr(Schema.String),
  evidences: Schema.Array(
    Schema.Struct({
      text: Schema.String,
      page: Schema.OptionFromNullOr(Schema.Number),
    })
  ),
});

export const AnnualReportListItemSchema = Schema.Struct({
  id: UUID,
  orgnr: Schema.OptionFromNullOr(Schema.String),
  fileName: Schema.String,
  status: Schema.Literals(["pending", "processing", "completed", "failed"]),
  reportType: Schema.Literals(["annual_report", "interim_report", "quarterly_report"]),
  fiscalYear: Schema.OptionFromNullOr(Schema.Number),
  createdAt: Schema.OptionFromNullOr(DateTimeUtcFromString),
  analyzedAt: Schema.OptionFromNullOr(DateTimeUtcFromString),
  progress: Schema.OptionFromNullOr(AnnualReportProcessingProgressSchema),
  eventCount: Schema.Number,
  events: Schema.Array(AnnualReportEventItemSchema),
});

export const AnnualReportListResponseSchema = Schema.Struct({
  metadata: Schema.Struct({
    totalCount: Schema.Number,
    page: Schema.Number,
    pageSize: Schema.Number,
    totalPages: Schema.Number,
    hasNextPage: Schema.Boolean,
    hasPreviousPage: Schema.Boolean,
  }),
  data: Schema.Array(AnnualReportListItemSchema),
});

const SortBySchema = Schema.Literals(["analyzedAt", "createdAt", "fiscalYear", "orgnr", "status"]);

const SortOrderSchema = Schema.Literals(["asc", "desc"]);

export const AnnualReportsV3Group = HttpApiGroup.make("annualReportsV3")
  .add(
    HttpApiEndpoint.post("uploadReport", "/annual-reports/upload", {
      payload: ReportUploadPayloadSchema,
      success: ReportUploadResponseSchema.pipe(HttpApiSchema.status(202)),
      error: [
        HttpApiError.BadRequestNoContent,
        HttpApiError.ConflictNoContent,
        HttpApiError.InternalServerErrorNoContent,
      ],
    })
  )
  .add(
    HttpApiEndpoint.post("importReport", "/annual-reports/import", {
      payload: ReportImportPayloadSchema,
      success: ReportUploadResponseSchema.pipe(HttpApiSchema.status(202)),
      error: [
        HttpApiError.BadRequestNoContent,
        HttpApiError.ConflictNoContent,
        HttpApiError.InternalServerErrorNoContent,
      ],
    })
  )
  .add(
    HttpApiEndpoint.get("getReportStatus", "/annual-reports/status/:uploadId", {
      params: { uploadId: uploadIdParam },
      success: ReportStatusResponseSchema,
      error: [
        HttpApiError.BadRequestNoContent,
        HttpApiError.NotFoundNoContent,
        HttpApiError.InternalServerErrorNoContent,
      ],
    })
  )
  .add(
    HttpApiEndpoint.get("listReports", "/annual-reports", {
      query: Schema.Struct({
        offset: Schema.NumberFromString.pipe(
          Schema.withDecodingDefaultType(Effect.sync(() => 0)),
          Schema.withConstructorDefault(Effect.sync(() => 0))
        ),
        limit: Schema.NumberFromString.pipe(
          clamp(1, 100),
          Schema.annotateEncoded({ description: "a number between 1 and 100" })
        ).pipe(
          Schema.withDecodingDefaultType(Effect.sync(() => 25)),
          Schema.withConstructorDefault(Effect.sync(() => 25))
        ),
        sortBy: SortBySchema.pipe(
          Schema.withDecodingDefaultType(Effect.sync(() => "analyzedAt" as const)),
          Schema.withConstructorDefault(Effect.sync(() => "analyzedAt" as const))
        ),
        sortOrder: SortOrderSchema.pipe(
          Schema.withDecodingDefaultType(Effect.sync(() => "desc" as const)),
          Schema.withConstructorDefault(Effect.sync(() => "desc" as const))
        ),
        orgnr: Schema.OptionFromOptional(Schema.String),
      }),
      success: AnnualReportListResponseSchema,
      error: [HttpApiError.BadRequestNoContent, HttpApiError.InternalServerErrorNoContent],
    })
  );
